import type { SalesMachineDetail, SalesOfferPositionRecord, SalesOrderRecord } from '@forq/sales-data';
import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Verkaufs-Lieferschein als druckfertiges HTML im FTW-Layout, gerendert aus dem Verkaufsauftrag:
 * Positionen OHNE Preise (Maschinen mit Maschinennummer), Empfänger, Empfangsbestätigung. Gleicher
 * Briefkopf/Fußzeile wie die übrigen Belege (aus dem Firmenprofil). Die LS-Nummer stammt aus der
 * gemeinsamen firmenweiten Lieferschein-Serie (siehe forq.sales_delivery_create). Gedruckt/als PDF
 * über den Browser (Button im erzeugten HTML).
 */

export interface SalesDeliveryContext {
  /** LS-Nummer aus der gemeinsamen Lieferschein-Serie (LS-2026-...). */
  readonly documentNumber: string;
  readonly order: SalesOrderRecord;
  readonly company: TenantCompanyProfile;
  /** Ausstellungsdatum (ISO). */
  readonly issuedIso: string;
}

// --- Formatierung -----------------------------------------------------------

const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
function qty(value: number): string { return qtyFormat.format(value); }
const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euroFormat.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = (value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

// --- Briefkopf / Fußzeile (aus dem Firmenprofil) ---------------------------

function letterhead(company: TenantCompanyProfile): string {
  const identity = company.identity;
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${col('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
}

// --- Bausteine --------------------------------------------------------------

function recipientBlock(order: SalesOrderRecord): string {
  const name = order.customerName || order.prospectName || '–';
  // Lieferanschrift aus der Verkaufsmeldung, sonst nur der Kundenname.
  const lines = order.delivery && order.delivery.address
    ? [name, ...order.delivery.address.split('\n').map((line) => line.trim())]
    : [name];
  return lines.filter(Boolean).map((line) => esc(line)).join('<br>');
}

function positionsTable(order: SalesOrderRecord): string {
  const rows = order.positions
    .filter((position) => position.kind !== 'text' || position.description)
    .map((position) => `<tr>
      <td class="c">${position.positionNumber}</td>
      <td>${esc(position.description)}${position.kind === 'machine' && position.sourceNumber ? `<div class="postext">Masch.-Nr. ${esc(position.sourceNumber)}</div>` : ''}</td>
      <td class="num">${position.kind === 'text' ? '' : qty(position.quantity)}</td>
      <td>${esc(position.unit)}</td>
    </tr>`).join('');
  return `<table class="pos">
    <thead><tr><th class="c">Pos.</th><th>Bezeichnung</th><th class="num">Menge</th><th>Einheit</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function body(order: SalesOrderRecord): string {
  return `
    <div class="parties">
      <div><span class="pl">Empfänger</span>${recipientBlock(order)}</div>
      <div><span class="pl">Verkaufsauftrag</span>${esc(order.number)}${order.businessCaseNumber ? `<br>Vorgang ${esc(order.businessCaseNumber)}` : ''}</div>
    </div>
    <p class="intro">Lieferung zum Verkaufsauftrag „${esc(order.title)}":</p>
    ${positionsTable(order)}
    <div class="sign">
      <div><span></span>Ware vollständig und unbeschädigt erhalten</div>
      <div><span></span>Ort, Datum, Unterschrift Empfänger</div>
    </div>`;
}

function shell(ctx: SalesDeliveryContext): string {
  const { company, order, documentNumber, issuedIso } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>Lieferschein ${esc(documentNumber)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; min-height: 273mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); display: flex; flex-direction: column; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .parties { display: flex; justify-content: space-between; gap: 12mm; margin-top: 8mm; font-size: 11.5px; }
  .parties .pl { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
  .intro { margin: 7mm 0 3mm; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 11px; }
  table.pos th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.pos td.c, table.pos th.c { text-align: center; width: 10mm; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.pos .postext { margin-top: 3px; font-size: 10.5px; color: #555; }
  .sign { display: flex; gap: 14mm; margin-top: 16mm; }
  .sign div { flex: 1; font-size: 10px; color: #555; }
  .sign span { display: block; border-bottom: 1px solid #333; margin-bottom: 4px; height: 12mm; }
  .footer { margin-top: auto; padding-top: 5px; border-top: 1.5px solid var(--accent); display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; min-height: 250mm; }
    @page { size: A4; margin: 12mm 14mm; }
    table.pos thead { display: table-header-group; }
    table.pos tr, .sign { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div class="sender">${esc(company.identity.name || '')}</div>
      <div class="doc-head">
        <div class="doc-title">Lieferschein</div>
        <table class="meta">
          <tr><th>Beleg-Nr.:</th><td>${esc(documentNumber)}</td></tr>
          <tr><th>Auftrag:</th><td>${esc(order.number)}</td></tr>
          <tr><th>Datum:</th><td>${dateLabel(issuedIso)}</td></tr>
          ${order.customerNumber ? `<tr><th>Kunden-Nr.:</th><td>${esc(order.customerNumber)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    ${body(order)}
    ${footer(company)}
  </div>
</body></html>`;
}

/** Das vollständige HTML eines Verkaufs-Lieferscheins -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderSalesDeliveryHtml(ctx: SalesDeliveryContext): string {
  return shell(ctx);
}

// --- UVV-Prüfbuch (Auslieferungsdurchsicht der Werkstatt) -------------------

export interface SalesUvvContext {
  readonly order: SalesOrderRecord;
  readonly company: TenantCompanyProfile;
  readonly issuedIso: string;
  /** Zugehörige Lieferschein-Nummer als Referenz auf dem Prüfbuch (optional). */
  readonly deliveryNumber?: string;
}

// Standard-Prüfumfang der wiederkehrenden Prüfung für Flurförderzeuge (DGUV Vorschrift 68).
// Vom System aufgedruckt, die Bewertung (i.O./Mangel/Bemerkung) trägt die Werkstatt vor Ort ein.
const UVV_CHECKLIST: readonly string[] = [
  'Typenschild, Tragfähigkeitsschild, Kennzeichnung',
  'Bremsanlage (Betriebs- und Feststellbremse)',
  'Lenkung',
  'Fahrantrieb und Fahrpedal',
  'Hubgerüst, Hubmast, Neigeeinrichtung',
  'Gabelzinken, Gabelträger, Lastaufnahmemittel',
  'Hubketten und Kettenrollen',
  'Hydraulikanlage (Dichtheit, Schläuche, Zylinder)',
  'Räder und Bereifung',
  'Fahrerschutzdach / Lastschutzgitter',
  'Sicherheitseinrichtungen (Hupe, Rückfahrwarnung)',
  'Beleuchtung und Signaleinrichtungen',
  'Fahrersitz und Rückhaltesystem (Sicherheitsgurt)',
  'Batterie/Ladegerät bzw. Motor und Abgasanlage',
  'Betriebsflüssigkeiten und Ölstände',
];

function uvvMachineRows(order: SalesOrderRecord): string {
  const machines = order.positions.filter((position) => position.kind === 'machine');
  if (machines.length === 0) {
    return '<tr><th>Maschine</th><td>–</td></tr>';
  }
  return machines
    .map((position) => `<tr><th>Maschine</th><td>${esc(position.description)}${position.sourceNumber ? ` · Masch.-Nr. ${esc(position.sourceNumber)}` : ''}</td></tr>`)
    .join('');
}

function uvvChecklistRows(): string {
  return UVV_CHECKLIST
    .map((item, index) => `<tr>
      <td class="c">${index + 1}</td>
      <td>${esc(item)}</td>
      <td class="box"></td>
      <td class="box"></td>
      <td></td>
    </tr>`)
    .join('');
}

function uvvShell(ctx: SalesUvvContext): string {
  const { company, order, issuedIso, deliveryNumber } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>UVV-Prüfbuch ${esc(order.number)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; min-height: 273mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); display: flex; flex-direction: column; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  table.info { border-collapse: collapse; border: 1px solid #ccc; margin-top: 8mm; width: 100%; }
  table.info th { text-align: left; color: var(--label); font-weight: 700; padding: 4px 8px; border: 1px solid #e2e2e2; background: #fafafa; width: 34mm; }
  table.info td { padding: 4px 8px; border: 1px solid #e2e2e2; }
  .intro { margin: 7mm 0 3mm; }
  table.uvv { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 11px; }
  table.uvv th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.uvv td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.uvv td.c, table.uvv th.c { text-align: center; width: 9mm; }
  table.uvv th.mark, table.uvv td.box { text-align: center; width: 16mm; }
  table.uvv td.box::after { content: ""; display: inline-block; width: 12px; height: 12px; border: 1px solid #555; }
  .sign { display: flex; gap: 14mm; margin-top: 14mm; }
  .sign div { flex: 1; font-size: 10px; color: #555; }
  .sign span { display: block; border-bottom: 1px solid #333; margin-bottom: 4px; height: 12mm; }
  .footer { margin-top: auto; padding-top: 5px; border-top: 1.5px solid var(--accent); display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; min-height: 250mm; }
    @page { size: A4; margin: 12mm 14mm; }
    table.uvv thead { display: table-header-group; }
    table.uvv tr, .sign { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div class="sender">${esc(company.identity.name || '')}</div>
      <div class="doc-head">
        <div class="doc-title">UVV-Prüfbuch</div>
        <table class="meta">
          <tr><th>Auftrag:</th><td>${esc(order.number)}</td></tr>
          ${deliveryNumber ? `<tr><th>Lieferschein:</th><td>${esc(deliveryNumber)}</td></tr>` : ''}
          <tr><th>Datum:</th><td>${dateLabel(issuedIso)}</td></tr>
          ${order.customerNumber ? `<tr><th>Kunden-Nr.:</th><td>${esc(order.customerNumber)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    <table class="info">
      <tr><th>Kunde</th><td>${esc(order.customerName || order.prospectName || '–')}</td></tr>
      ${uvvMachineRows(order)}
    </table>
    <p class="intro">Auslieferungsdurchsicht / wiederkehrende Prüfung nach DGUV Vorschrift 68:</p>
    <table class="uvv">
      <thead><tr><th class="c">Nr.</th><th>Prüfpunkt</th><th class="mark">i.O.</th><th class="mark">Mangel</th><th>Bemerkung</th></tr></thead>
      <tbody>${uvvChecklistRows()}</tbody>
    </table>
    <div class="sign">
      <div><span></span>Festgestellte Mängel / Maßnahmen</div>
      <div><span></span>Prüfer (Werkstatt), Ort, Datum</div>
    </div>
    ${footer(company)}
  </div>
</body></html>`;
}

/** Das vollständige HTML des UVV-Prüfbuchs zur Auslieferungsdurchsicht -- iframe (srcDoc) + Drucken. */
export function renderSalesUvvHtml(ctx: SalesUvvContext): string {
  return uvvShell(ctx);
}

// --- Angebot (im FTW-Angebots-Stil) ----------------------------------------

/** Generische Angebotsdaten -- funktioniert für ein gespeichertes Angebot UND ein Live-Paketangebot. */
export interface SalesOfferDocumentContext {
  readonly documentNumber: string;
  /** Empfängerblock: Name + Anschrift (vom Aufrufer aus dem Kundenstamm gebaut). */
  readonly recipientLines: readonly string[];
  readonly contactName: string;
  readonly validUntil: string | null;
  readonly positions: readonly SalesOfferPositionRecord[];
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  readonly company: TenantCompanyProfile;
  readonly issuedIso: string;
  /** Volle Maschinendaten je machineId (Technik + Foto) für den Datenblock unter Maschinenpositionen. */
  readonly machineDetails: Readonly<Record<string, SalesMachineDetail>>;
}

function machineDetailBlock(detail: SalesMachineDetail): string {
  const base: { readonly label: string; readonly value: string; readonly unit: string }[] = [
    { label: 'Hersteller', value: detail.manufacturer, unit: '' },
    { label: 'Typ', value: detail.model, unit: '' },
    ...(detail.serialNumber ? [{ label: 'Seriennummer', value: detail.serialNumber, unit: '' }] : []),
    ...(detail.constructionYear ? [{ label: 'Baujahr', value: String(detail.constructionYear), unit: '' }] : []),
  ];
  // In feste 3er-Reihen als Tabelle rendern statt per CSS-Grid: die Druck-Engine (window.print → PDF)
  // stellt Grid-Zellen teils diagonal versetzt dar. Eine Tabelle mit table-layout:fixed richtet die
  // Spalten in jedem Druckpfad sauber an einer gemeinsamen Grundlinie aus.
  const specs = [...base, ...detail.specs];
  const columns = 3;
  const rows: string[] = [];
  for (let i = 0; i < specs.length; i += columns) {
    const group = specs.slice(i, i + columns);
    const filled = group
      .map((spec) => `<td class="mb-cell"><span class="mb-label">${esc(spec.label)}</span><span class="mb-value">${esc(spec.value)}${spec.unit ? ` ${esc(spec.unit)}` : ''}</span></td>`)
      .join('');
    const padding = '<td class="mb-cell"></td>'.repeat(columns - group.length);
    rows.push(`<tr>${filled}${padding}</tr>`);
  }
  const photo = detail.imageUrl
    ? `<div class="mb-photo"><img src="${esc(detail.imageUrl)}" alt="${esc(`${detail.manufacturer} ${detail.model}`)}"></div>`
    : '';
  return `<div class="machine-block">${photo}<div class="mb-body"><table class="mb-grid">${rows.join('')}</table></div></div>`;
}

function offerPositionsTable(ctx: SalesOfferDocumentContext): string {
  const rows = ctx.positions
    .filter((position) => position.kind !== 'text' || position.description)
    .map((position) => {
      const priced = position.kind !== 'text';
      const detail = position.machineId ? ctx.machineDetails[position.machineId] : undefined;
      const main = `<tr>
        <td class="c">${position.positionNumber}</td>
        <td>${esc(position.description)}</td>
        <td class="num">${priced ? qty(position.quantity) : ''}</td>
        <td>${esc(position.unit)}</td>
        <td class="num">${priced ? money(position.unitPrice) : ''}</td>
        <td class="num">${priced ? money(position.netAmount) : ''}</td>
      </tr>`;
      const block = detail ? `<tr class="pos-detail"><td></td><td colspan="5">${machineDetailBlock(detail)}</td></tr>` : '';
      return main + block;
    })
    .join('');
  return `<table class="pos">
    <thead><tr><th class="c">Pos.</th><th>Artikel / Leistung</th><th class="num">Menge</th><th>Einheit</th><th class="num">E-Preis</th><th class="num">Endpreis</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function offerShell(ctx: SalesOfferDocumentContext): string {
  const { company, documentNumber, issuedIso } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  const recipient = ctx.recipientLines.filter(Boolean).map((line) => esc(line)).join('<br>') || '–';
  const validText = ctx.validUntil ? dateLabel(ctx.validUntil) : '—';
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>Angebot ${esc(documentNumber)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; min-height: 273mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); display: flex; flex-direction: column; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .sender { font-size: 8px; color: #555; text-decoration: underline; margin-bottom: 3mm; }
  .recipient { font-weight: 700; font-size: 12px; line-height: 1.5; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 20px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .intro { margin: 8mm 0 1mm; }
  .terms { margin: 0 0 3mm; font-size: 10.5px; color: #444; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 4mm; font-size: 11px; }
  table.pos th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos > tbody > tr > td { padding: 6px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.pos td.c, table.pos th.c { text-align: center; width: 10mm; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.pos tr.pos-detail > td { border-bottom: 1px solid #e6e6e6; padding-top: 0; }
  .machine-block { display: flex; gap: 6mm; padding: 4px 0 6px; }
  .mb-photo { flex: none; width: 42mm; }
  .mb-photo img { width: 100%; max-height: 42mm; object-fit: contain; }
  .mb-body { flex: 1; min-width: 0; }
  .mb-grid { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; }
  .mb-cell { width: 33.33%; padding: 1px 6mm 3px 0; vertical-align: top; }
  .mb-label { display: block; color: #888; font-size: 8px; text-transform: uppercase; letter-spacing: .03em; }
  .mb-value { display: block; font-weight: 600; }
  .mb-notes { margin-top: 3mm; font-size: 10px; font-weight: 600; }
  .mb-notes .mb-label { display: block; margin-bottom: 1px; font-weight: 400; }
  table.totals { align-self: flex-end; margin: 5mm 0 0; border-collapse: collapse; min-width: 82mm; font-size: 11.5px; }
  table.totals th { text-align: left; color: #444; font-weight: 500; padding: 4px 14px 4px 10px; }
  table.totals td { text-align: right; font-weight: 700; padding: 4px 10px; white-space: nowrap; }
  table.totals tr.is-strong th, table.totals tr.is-strong td { border-top: 1.5px solid var(--accent); font-size: 14px; color: var(--accent); padding-top: 6px; }
  .note { font-size: 10px; color: #444; margin: 6mm 0 0; }
  .footer { margin-top: auto; padding-top: 5px; border-top: 1.5px solid var(--accent); display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; min-height: 250mm; }
    @page { size: A4; margin: 12mm 14mm; }
    table.pos thead { display: table-header-group; }
    table.pos tr, .machine-block, table.totals { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div>
        <div class="sender">${esc(`${company.identity.name || ''}, ${company.identity.address.street || ''}, ${`${company.identity.address.postalCode || ''} ${company.identity.address.city || ''}`.trim()}`)}</div>
        <div class="recipient">${recipient}</div>
      </div>
      <div class="doc-head">
        <div class="doc-title">ANGEBOT</div>
        <table class="meta">
          <tr><th>Nummer:</th><td>${esc(documentNumber)}</td></tr>
          <tr><th>Datum:</th><td>${dateLabel(issuedIso)}</td></tr>
          ${ctx.contactName ? `<tr><th>Ansprechpartner:</th><td>${esc(ctx.contactName)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    <p class="intro">Nachfolgend bieten wir Ihnen die aufgeführten Positionen an.</p>
    <p class="terms">Gültig bis zum ${esc(validText)}. Zahlung sofort ohne Abzug nach Rechnungserhalt.</p>
    ${offerPositionsTable(ctx)}
    <table class="totals">
      <tr><th>Netto-Betrag</th><td>${money(ctx.netTotal)}</td></tr>
      <tr><th>MwSt. (19 %)</th><td>${money(ctx.vatTotal)}</td></tr>
      <tr class="is-strong"><th>Gesamtbetrag</th><td>${money(ctx.grossTotal)}</td></tr>
    </table>
    <p class="note">Die Ware bleibt bis zur vollständigen Bezahlung Eigentum der ${esc(company.identity.name || 'Fördertechnik Werl GmbH')}. Es gelten unsere AGB.</p>
    ${footer(company)}
  </div>
</body></html>`;
}

/** Das vollständige HTML eines Angebots im FTW-Stil -- iframe (srcDoc) + Drucken. Auch fürs Paketangebot. */
export function renderSalesOfferHtml(ctx: SalesOfferDocumentContext): string {
  return offerShell(ctx);
}
